import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { ActivatedRoute, Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface WBSActivity {
  id?: number;
  activity_name: string;
  activity_code?: string;
  description?: string;
  plan_start_date: string | Date;
  plan_end_date: string | Date;
  actual_start_date?: string | Date;
  actual_end_date?: string | Date;
  percent_complete: number;
  assigned_to?: string;
  assigned_org_id?: number | null;
  assigned_organization_name?: string | null;
  activity_order?: number;
}

@Component({
  selector: 'app-work-breakdown-structure',
  templateUrl: './work-breakdown-structure.component.html',
  styleUrls: ['./work-breakdown-structure.component.css']
})
export class WorkBreakdownStructureComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  projectId: number | null = null;
  projectName: string = '';
  parentOrgName: string = '';
  activities: WBSActivity[] = [];
  isBidderView = false;
  isLoading = false;
  isImporting = false;
  searchTerm = '';
  dataSource = new MatTableDataSource<WBSActivity>([]);
  displayedColumns: string[] = [
    'activity_name',
    'plan_dates',
    'actual_dates',
    'percent_complete',
    'assigned_to',
    'assigned_organization',
    'actions'
  ];

  activityForm!: FormGroup;
  showForm = false;
  editingActivity: WBSActivity | null = null;
  selectedFile: File | null = null;
  staffList: any[] = [];
  isLoadingStaff: boolean = false;
  assignableOrganizations: { org_id: number; org_name: string }[] = [];
  isLoadingAssignableOrgs = false;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private toastr: ToastrMsgService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.initializeForm();
  }

  ngAfterViewInit(): void {
    this.connectTable();
  }

  private connectTable(): void {
    if (this.paginator) this.dataSource.paginator = this.paginator;
    if (this.sort) this.dataSource.sort = this.sort;
  }

  ngOnInit(): void {
    this.fetchStaff();
    this.route.params.subscribe(params => {
      this.projectId = params['projectId'] ? +params['projectId'] : null;
      this.isBidderView = this.route.snapshot.queryParamMap.get('bidderView') === 'true';
      this.displayedColumns = [
        'activity_name',
        'plan_dates',
        'actual_dates',
        'percent_complete',
        'assigned_to',
        'assigned_organization',
        'actions'
      ];
      this.applyBidderFormMode();
      if (this.projectId) {
        this.loadWBS();
        if (!this.isBidderView) {
          this.loadProjectDetails();
          this.loadAssignableOrganizations();
        } else {
          this.assignableOrganizations = [];
        }
      }
    });
  }

  loadAssignableOrganizations(): void {
    if (!this.projectId) return;
    this.isLoadingAssignableOrgs = true;
    this.apiService.getWbsAssignableOrganizations(this.projectId).subscribe({
      next: (res: any) => {
        this.assignableOrganizations = res.data || [];
        this.isLoadingAssignableOrgs = false;
      },
      error: () => {
        this.assignableOrganizations = [];
        this.isLoadingAssignableOrgs = false;
      }
    });
  }

  fetchStaff(): void {
    this.isLoadingStaff = true;
    const params = { keyword: "" };
    this.apiService.getStaff(params).subscribe({
      next: (res: any) => {
        this.staffList = res.data || [];
        this.isLoadingStaff = false;
      },
      error: (err) => {
        console.error('Error fetching staff:', err);
        this.isLoadingStaff = false;
        this.toastr.error('Failed to load staff members');
      }
    });
  }

  getSelectedStaffName(staffId: string | number): string {
    if (!staffId) return '';
    const selectedStaff = this.staffList.find(staff => 
      staff.id === staffId || 
      staff.personnel_id === staffId ||
      staff.full_name === staffId
    );
    return selectedStaff ? (selectedStaff.full_name || `${selectedStaff.first_name} ${selectedStaff.last_name}`) : String(staffId);
  }

  initializeForm(): void {
    this.activityForm = this.fb.group({
      activity_name: ['', Validators.required],
      activity_code: [''],
      description: [''],
      plan_start_date: [null, Validators.required],
      plan_end_date: [null, Validators.required],
      actual_start_date: [null],
      actual_end_date: [null],
      percent_complete: [0, [Validators.min(0), Validators.max(100)]],
      assigned_to: [''],
      assigned_org_id: [null as number | null],
      activity_order: [0]
    });
  }

  private applyBidderFormMode(): void {
    const nameCtrl = this.activityForm.get('activity_name');
    const planStart = this.activityForm.get('plan_start_date');
    const planEnd = this.activityForm.get('plan_end_date');
    if (this.isBidderView) {
      nameCtrl?.clearValidators();
      planStart?.clearValidators();
      planEnd?.clearValidators();
    } else {
      nameCtrl?.setValidators([Validators.required]);
      planStart?.setValidators([Validators.required]);
      planEnd?.setValidators([Validators.required]);
    }
    nameCtrl?.updateValueAndValidity({ emitEvent: false });
    planStart?.updateValueAndValidity({ emitEvent: false });
    planEnd?.updateValueAndValidity({ emitEvent: false });
  }

  loadProjectDetails(): void {
    if (!this.projectId) return;
    this.apiService.getProjectById(this.projectId).subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          this.projectName = response.data.project_name || 'Unknown Project';
        }
      },
      error: (error) => {
        console.error('Error loading project details:', error);
      }
    });
  }

  loadWBS(): void {
    if (!this.projectId) return;

    const bidderView = this.route.snapshot.queryParamMap.get('bidderView') === 'true';
    this.isLoading = true;
    this.apiService.getWBSByProject(this.projectId, bidderView).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response.success) {
          this.activities = response.data || [];
          this.dataSource.data = this.activities;
          if (response.project?.project_name) {
            this.projectName = response.project.project_name;
          }
          this.parentOrgName = response.project?.parent_org_name || '';
          setTimeout(() => this.connectTable());
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading WBS:', error);
        this.activities = [];
        this.dataSource.data = [];
        if (error?.status === 403 && this.isBidderView) {
          this.toastr.error(error.error?.message || 'Not allowed to view allocated WBS for this project');
          return;
        }
        this.toastr.error('Failed to load work breakdown structure');
      }
    });
  }

  get activityCount(): number {
    return this.activities.length;
  }

  get overallPercent(): number {
    if (!this.activities.length) return 0;
    const sum = this.activities.reduce((total, activity) => total + (Number(activity.percent_complete) || 0), 0);
    return Math.round(sum / this.activities.length);
  }

  get unassignedOrgCount(): number {
    return this.activities.filter((activity) => !activity.assigned_organization_name).length;
  }

  openAddForm(): void {
    this.editingActivity = null;
    this.activityForm.reset({
      percent_complete: 0,
      activity_order: this.activities.length,
      assigned_org_id: null
    });
    this.showForm = true;
  }

  openEditForm(activity: WBSActivity): void {
    this.editingActivity = activity;
    // Convert date strings to Date objects for the form
    // For assigned_to, try to find the staff ID if it's a name
    let assignedToValue = activity.assigned_to;
    if (activity.assigned_to && this.staffList.length > 0) {
      const staff = this.staffList.find(s => 
        s.full_name === activity.assigned_to || 
        `${s.first_name} ${s.last_name}` === activity.assigned_to ||
        s.id?.toString() === activity.assigned_to ||
        s.personnel_id?.toString() === activity.assigned_to
      );
      if (staff) {
        assignedToValue = staff.id || staff.personnel_id;
      }
    }
    
    const formData = {
      ...activity,
      plan_start_date: activity.plan_start_date ? new Date(activity.plan_start_date) : null,
      plan_end_date: activity.plan_end_date ? new Date(activity.plan_end_date) : null,
      actual_start_date: activity.actual_start_date ? new Date(activity.actual_start_date) : null,
      actual_end_date: activity.actual_end_date ? new Date(activity.actual_end_date) : null,
      assigned_to: assignedToValue
    };
    this.activityForm.patchValue(formData);
    this.showForm = true;
  }

  closeForm(): void {
    this.showForm = false;
    this.editingActivity = null;
    this.activityForm.reset();
  }

  saveActivity(): void {
    if (this.activityForm.invalid || !this.projectId) {
      this.toastr.error('Please fill in all required fields');
      return;
    }
    if (this.isBidderView && !this.editingActivity?.id) {
      this.toastr.error('Allocated activities can only be updated, not created');
      return;
    }

    // Format dates to ISO string format (YYYY-MM-DD)
    const formatDateForBackend = (date: Date | string | null): string | null => {
      if (!date) return null;
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return null;
      return d.toISOString().split('T')[0];
    };

    // Get staff name for assigned_to if ID is selected
    let assignedToValue = this.activityForm.value.assigned_to;
    if (assignedToValue && this.staffList.length > 0) {
      const selectedStaff = this.staffList.find(s => 
        s.id === assignedToValue || s.personnel_id === assignedToValue
      );
      if (selectedStaff) {
        assignedToValue = selectedStaff.full_name || `${selectedStaff.first_name} ${selectedStaff.last_name}`;
      }
    }

    const rawOrgId = this.activityForm.value.assigned_org_id;
    const assignedOrgId =
      rawOrgId === null || rawOrgId === undefined || rawOrgId === ''
        ? null
        : Number(rawOrgId);

    const formValue = this.isBidderView
      ? {
          actual_start_date: formatDateForBackend(this.activityForm.value.actual_start_date),
          actual_end_date: formatDateForBackend(this.activityForm.value.actual_end_date),
          percent_complete: this.activityForm.value.percent_complete,
          assigned_to: assignedToValue || null
        }
      : {
          ...this.activityForm.value,
          project_id: this.projectId,
          plan_start_date: formatDateForBackend(this.activityForm.value.plan_start_date),
          plan_end_date: formatDateForBackend(this.activityForm.value.plan_end_date),
          actual_start_date: formatDateForBackend(this.activityForm.value.actual_start_date),
          actual_end_date: formatDateForBackend(this.activityForm.value.actual_end_date),
          assigned_to: assignedToValue || null,
          assigned_org_id: Number.isFinite(assignedOrgId) ? assignedOrgId : null
        };

    if (this.editingActivity?.id) {
      // Update existing
      this.apiService.updateWBSActivity(this.editingActivity.id, formValue).subscribe({
        next: (response: any) => {
          if (response.success) {
            this.toastr.success(this.isBidderView ? 'Progress updated' : 'Activity updated successfully');
            this.loadWBS();
            this.closeForm();
          }
        },
        error: (error) => {
          console.error('Error updating activity:', error);
          this.toastr.error(error.error?.message || 'Failed to update activity');
        }
      });
    } else {
      // Create new
      this.apiService.createWBSActivity(formValue).subscribe({
        next: (response: any) => {
          if (response.success) {
            this.toastr.success('Activity created successfully');
            this.loadWBS();
            this.closeForm();
          }
        },
        error: (error) => {
          console.error('Error creating activity:', error);
          this.toastr.error('Failed to create activity');
        }
      });
    }
  }

  deleteActivity(activity: WBSActivity): void {
    if (!activity.id) return;

    if (confirm(`Are you sure you want to delete "${activity.activity_name}"?`)) {
      this.apiService.deleteWBSActivity(activity.id).subscribe({
        next: (response: any) => {
          if (response.success) {
            this.toastr.success('Activity deleted successfully');
            this.loadWBS();
          }
        },
        error: (error) => {
          console.error('Error deleting activity:', error);
          this.toastr.error('Failed to delete activity');
        }
      });
    }
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        this.selectedFile = file;
      } else {
        this.clearSelectedFile();
        this.toastr.error('Please select a valid Excel file (.xlsx or .xls)');
      }
    }
  }

  importExcel(): void {
    if (!this.selectedFile || !this.projectId) {
      this.toastr.error('Please select a file and ensure project is selected');
      return;
    }

    this.isImporting = true;
    this.apiService.importWBSFromExcel(this.projectId, this.selectedFile).subscribe({
      next: (response: any) => {
        this.isImporting = false;
        if (response.success) {
          this.toastr.success(`Imported ${response.data?.length || 0} activities successfully`);
          this.clearSelectedFile();
          this.loadWBS();
        }
      },
      error: (error) => {
        this.isImporting = false;
        console.error('Error importing Excel:', error);
        this.toastr.error('Failed to import Excel file');
      }
    });
  }

  clearSelectedFile(): void {
    this.selectedFile = null;
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  exportExcel(): void {
    if (!this.projectId) {
      this.toastr.error('Project ID is required');
      return;
    }

    this.apiService.exportWBSToExcel(this.projectId).subscribe({
      next: (blob: Blob) => {
        const fileName = `WBS_Project_${this.projectId}_${new Date().getTime()}.xlsx`;
        saveAs(blob, fileName);
        this.toastr.success('Excel file downloaded successfully');
      },
      error: (error) => {
        console.error('Error exporting Excel:', error);
        this.toastr.error('Failed to export Excel file');
      }
    });
  }

  getCompletionClass(percent: number): string {
    if (percent >= 100) return 'complete';
    if (percent >= 75) return 'high';
    if (percent >= 50) return 'medium';
    if (percent >= 25) return 'low';
    return 'none';
  }

  formatDate(date: string | Date | null | undefined): string {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  formatDateRange(start: string | Date | null | undefined, end: string | Date | null | undefined): string {
    const startLabel = this.formatDate(start);
    const endLabel = this.formatDate(end);
    if (startLabel === '—' && endLabel === '—') return '—';
    return `${startLabel} → ${endLabel}`;
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchTerm = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  viewDeviationReport(): void {
    if (this.projectId) {
      this.router.navigate(['/home/work-breakdown-structure/deviation-report'], {
        queryParams: { project_id: this.projectId }
      });
    } else {
      this.router.navigate(['/home/work-breakdown-structure/deviation-report']);
    }
  }
}

